export enum ReportTypes {
  student = 'student',
  class = 'class',
}

export interface GenerateReportProps {
  reportType: ReportTypes;
  currentClassroom?: any;
  currentStudent?: any;
  children?: any;
}

export interface ClassReportContent {
  numberOfStudents: number;
  reportDate: string;
  averageActivitiesCompleted: number;
  averageStudentProgress: number;
  passRate: number;
  failureRate: number;
  teachers?: {
    id: string;
    firstName: string;
    lastName: string;
  }[];
  averageSudentsPrograssPerLevel: Array<{
    level: number;
    averageSudentsPrograss: number;
  }>;
  averageTimeSpentPerLevels: Array<{
    level: number;
    averageTimeSpent: number;
  }>;
  averageStreakLengthPerLevel: Array<{
    level: number;
    averageStreakLength: number;
  }>;
  averageActivityAttemptsPerLevel: Array<{
    level: number;
    averageActivityAttempts: number;
  }>;
  averageSkillScore: Array<{
    skillId: number;
    averageScore: number;
  }>;
}

export enum StudentMetricsDashboardTimeRange {
  all_time = 'AllTime',
  last_7_days = 'Last7Days',
  last_30_days = 'Last30Days',
  this_year = 'ThisYear',
}

export interface StudentMetricsDashboardFilters {
  timeRange?: StudentMetricsDashboardTimeRange;
  levelIds?: number[];
  groupIds?: string[];
  teacherIds?: string[];
  studentIds?: string[];
  gradeIds?: number[];
  skillFilters?: {
    skillId: number;
    performanceStatus: 'Pass' | 'Fail';
  }[];
}

export interface StudentMetricsDashboardKpis {
  studentsThatPassedLevel: number;
  passRate: number;
  totalActivitiesPassed: number;
  failureRate: number;
  totalTimeSpentSeconds: number;
  averageTimeSpentSeconds: number;
}

export interface StudentMetricsDashboardProgressAtCurrentLevel {
  level: number;
  averageProgressPercent: number;
  studentCount: number;
}

export interface StudentMetricsDashboardAverageScoresPerSkill {
  skillId: number;
  skillName: string;
  averageScore: number;
  studentCount: number;
}

export interface StudentMetricsDashboardLevelTransitions {
  currentLevel: number;
  currentLevelName: string;
  startingLevel: number;
  startingLevelName: string;
  studentCount: number;
  percentage: number;
}

export interface StudentMetricsDashboardPlacementDistribution {
  level: number;
  levelName: string;
  studentCount: number;
  percentage: number;
}

export interface StudentMetricsDashboardAverageStudentGrowth {
  month: string;
  averageGrowth: number;
}

export interface StudentMetricsDashboardAcrossSkills {
  skillId: number;
  skillName: string;
  passCount: number;
  failCount: number;
  passPercentage: number;
  failPercentage: number;
}

export interface StudentMetricsDashboardContent {
  totalStudents: number;
  kpis?: StudentMetricsDashboardKpis;
  charts?: {
    studentProgressAtCurrentLevel?: StudentMetricsDashboardProgressAtCurrentLevel[];
    averageScoresPerSkill?: StudentMetricsDashboardAverageScoresPerSkill[];
    studentLevelTransitions?: StudentMetricsDashboardLevelTransitions[];
    studentPlacementDistribution?: StudentMetricsDashboardPlacementDistribution[];
    averageStudentGrowth?: StudentMetricsDashboardAverageStudentGrowth[];
    studentsAcrossSkills?: StudentMetricsDashboardAcrossSkills[];
  };
}

export type StudentSkillPerformanceSortByQueryParam =
  | 'classname'
  | 'currentlevel'
  | 'firstname'
  | 'lastname'
  | 'skill';

export interface StudentSkillPerformancePaginationQueryParams {
  page?: number;
  pageSize?: number;
  sortBy?: StudentSkillPerformanceSortByQueryParam;
  sortDirection?: 'asc' | 'desc';
  sortBySkillId?: number;
}

export type StudentSkillPerformanceQueryParams = StudentMetricsDashboardFilters &
  StudentSkillPerformancePaginationQueryParams;

export interface SkillPerformance {
  score: number;
  status: string;
}

export interface StudentSkillPerformance {
  id: string;
  firstName: string;
  lastName: string;
  classId: string;
  className: string;
  gradeId: string;
  gradeName: string;
  currentLevel: number;
  teachersIds: string[];
  teachers: string[];
  skillScores: {
    [skillId: number]: SkillPerformance;
  };
}

export interface StudentSkillPerformanceContent {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  skills: {
    [skillId: number]: string;
  };
  students: StudentSkillPerformance[];
}
